import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BookMeetingService } from '../../../../../core/services/book-meeting.service';
import { LocaleService } from '../../../../../core/services/locale.service';

/**
 * MeetingDetailsSectionComponent — title input only.
 * Date/time is now handled by DateTimeSectionComponent below this.
 * Matches prototype: plain input, green focus border, 14px font.
 *
 * READONLY MODE: when the panel is in 'view' mode (driven by
 * `BookMeetingService.mode`), the input is replaced by a heading-style
 * static rendering. Empty subjects show "(No subject)" as a muted hint.
 */
@Component({
  selector: 'im-meeting-details-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslocoPipe],
  template: `
    @if (readonly()) {
      <h3 class="mds__title-static" [class.mds__title-static--empty]="!service.subject()">
        {{ titleDisplay() }}
      </h3>
      @if (!service.viewerIsOrganizer() && service.loadedOrganizerName()) {
        <p class="mds__organiser">
          {{ 'viewer.book_meeting_panel.meeting_details.organised_by' | transloco: { name: service.loadedOrganizerName() } }}
        </p>
      }
    } @else {
      <input
        id="bmp-title-input"
        type="text"
        class="mds__title"
        [placeholder]="'viewer.book_meeting_panel.meeting_details.add_a_title' | transloco"
        [ngModel]="service.subject()"
        (ngModelChange)="service.subject.set($event)"
        autocomplete="off"
        data-testid="bmp-title-input"
        [attr.aria-label]="'viewer.book_meeting_panel.meeting_details.meeting_title' | transloco" />
    }
  `,
  styles: [`
    :host { display: block; }

    .mds__title {
      width: 100%;
      padding: 12px 14px;
      font-size: 14px;
      font-weight: 400;
      background: var(--focus-overlay);
      border: 1px solid var(--divider);
      border-radius: 10px;
      color: var(--text);
      outline: none;
      transition: border-color 0.15s, background 0.15s;

      &::placeholder {
        color: var(--text-tertiary);
      }

      &:focus {
        border-color: rgba(16, 185, 129, 0.25);
        background: var(--pressed-overlay);
      }
    }

    .mds__title-static {
      margin: 0;
      padding: 4px 0 6px;
      font-size: 18px;
      font-weight: 600;
      line-height: 1.3;
      color: var(--text);
      letter-spacing: -0.01em;
    }

    .mds__title-static--empty {
      color: var(--text-tertiary);
      font-weight: 500;
    }

    .mds__organiser {
      margin: 2px 0 0;
      font-size: 12px;
      color: var(--text-tertiary);
    }

    @media (prefers-reduced-motion: reduce) {
      .mds__title { transition: none; }
    }
  `],
})
export class MeetingDetailsSectionComponent {
  protected readonly service = inject(BookMeetingService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);
  protected readonly readonly = computed(() => this.service.mode() === 'view');

  /** Title fallback chain for read-only meetings: subject → "(Private)" when
   *  the meeting is privacy-flagged → "{Organiser}'s meeting" when the viewer
   *  isn't the organiser → "(No subject)" as a last resort. Lets non-organiser
   *  views show meaningful context instead of an empty placeholder. */
  protected readonly titleDisplay = computed(() => {
    this.locale.locale();
    const subject = this.service.subject();
    if (subject) return subject;
    if (this.service.isPrivate()) return this.transloco.translate('viewer.book_meeting_panel.meeting_details.private_meeting');
    const organiser = this.service.loadedOrganizerName();
    if (organiser && !this.service.viewerIsOrganizer()) {
      return this.transloco.translate('viewer.book_meeting_panel.meeting_details.organisers_meeting', { name: organiser });
    }
    return this.transloco.translate('viewer.book_meeting_panel.meeting_details.no_subject');
  });
}
